import { Params, Router, UrlTree } from '@angular/router';
import { Observable, of, tap, throwError } from 'rxjs';
import { SimulationEngineResponse, SimulatorEngineRequest } from '../api/simulator-engine.api';
import { ResolutionContext } from './result-page.resolver';
import { ClientType } from '../service/type-detector.service';

export interface SimulationLoad {
  canHandle(ctx: ResolutionContext): boolean;
  execute(ctx: ResolutionContext): Observable<SimulationEngineResponse | boolean>;
}

class LoadSimulationByIdRule implements SimulationLoad {
  canHandle({ queryParams, stateSnapshot }: ResolutionContext): boolean {
    return (
      !!queryParams.simulationId && stateSnapshot.simulationId !== queryParams.simulationId && !queryParams.bundleCode
    );
  }
  execute({ queryParams, facade }: ResolutionContext): Observable<SimulationEngineResponse> {
    return facade.fetchSimulationById(queryParams.simulationId);
  }
}

class LoadSimulationWithIdAndBundle implements SimulationLoad {
  canHandle({ queryParams }: ResolutionContext): boolean {
    return !!queryParams.simulationId && !!queryParams.bundleCode;
  }
  execute({ queryParams, facade }: ResolutionContext): Observable<SimulationEngineResponse> {
    return facade
      .fetchSimulationById(queryParams.simulationId)
      .pipe(tap(() => facade.redirectFromWebsiteToOlreg(queryParams.bundleCode)));
  }
}

class CreateSimulationFromPayload implements SimulationLoad {
  canHandle({ queryParams }: ResolutionContext): boolean {
    return !!queryParams.simulationRequest;
  }
  execute({ queryParams, facade, router }: ResolutionContext): Observable<SimulationEngineResponse> {
    const parsed = this.parse(queryParams.simulationRequest);
    if (parsed instanceof Error) {
      return throwError(() => parsed);
    }
    return facade.createSimulationWithPayload(parsed).pipe(
      tap((simulation: SimulationEngineResponse) => {
        router.navigate(['/result'], {
          queryParams: { simulationId: simulation.simulationId },
          queryParamsHandling: 'merge',
        });
      }),
    );
  }

  parse(raw: string): SimulatorEngineRequest[] | Error {
    try {
      return JSON.parse(decodeURIComponent(raw)) as SimulatorEngineRequest[];
    } catch {
      return new Error('Invalid simulationRequest payload in query parameter');
    }
  }
}

class CreateSimulationFromState implements SimulationLoad {
  // Use case - applied when we do flow from start of simulator
  canHandle({ stateSnapshot, queryParams }: ResolutionContext): boolean {
    return stateSnapshot.numberOfStep === stateSnapshot.numberOfStepExecuted && !queryParams.simulationId;
  }
  execute({ facade, router }: ResolutionContext): Observable<SimulationEngineResponse> {
    return facade.createSimulationFromState().pipe(
      tap(() =>
        router.navigate(['/result'], {
          queryParams: { simulationId: facade.state$.value.simulationId },
          queryParamsHandling: 'merge',
        }),
      ),
    );
  }
}

class SimulationIsLoaded implements SimulationLoad {
  canHandle({ stateSnapshot }: ResolutionContext): boolean {
    return !!stateSnapshot.simulationId;
  }
  execute(): Observable<true> {
    return of(true);
  }
}

export class RedirectToBeginning implements SimulationLoad {
  // Fallback rule - always matches
  canHandle(): boolean {
    return true;
  }
  execute({ router, queryParams, facade }: ResolutionContext): Observable<true> {
    const urlTree = this.urlToAppStart(router, queryParams, facade.state$.value.type);
    router.navigateByUrl(urlTree);
    return of(true);
  }

  urlToAppStart(router: Router, queryParams: Params, type: ClientType): UrlTree {
    return router.createUrlTree([type === ClientType.B2C ? '/energy' : '/site-number'], { queryParams: queryParams });
  }
}

export const SIMULATION_LOAD_RULES: SimulationLoad[] = [
  // Order of rules is important, first match is executed
  new SimulationIsLoaded(),
  new LoadSimulationByIdRule(),
  new LoadSimulationWithIdAndBundle(),
  new CreateSimulationFromPayload(),
  new CreateSimulationFromState(),
  new RedirectToBeginning(), // Fallback rule
];
